<template>
  <div class="">
    <section class="about-item-area">
      <div class="container">
        <div class="bg-white" v-loading="isLoading">
          <div class="divider mt-5 mb-15"></div>
          <EventInfo @event-ready="onEventReady" />
          <el-divider></el-divider>

          <template v-if="event && event.closetime > Math.floor(Date.now() / 1000)">
            <!-- 展示票务选择 + 填写表单 -->
            <el-row :gutter="30">
              <!-- 左侧：票务列表 -->
              <el-col :span="10" :xs="24">
                <div v-if="validOrders.length > 0" class="registered-notice-block">
                  <el-alert
                    type="success"
                    show-icon
                    :closable="false"
                  >
                    <template #default>
                      <div class="alert-content-row">
                        <el-button
                          type="text"
                          class="alert-link-btn"
                          @click="$router.push('/register/result')"
                        >
                          {{ $t('register.viewRegisteredInfo') }}
                        </el-button>
                      </div>
                    </template>
                  </el-alert>
                </div>

                <div class="ticket-list-wrapper">
                  <div class="ticket-list-header">
                    <h4>{{ $t('register.ticket.title') }}</h4>
                  </div>
                  <div class="ticket-list">
                    <div
                      v-for="(ticket, index) in ticketList"
                      :key="index"
                      class="ticket-item"
                      :class="{ 'ticket-active': selectedTicketId === ticket.id }"
                      @click="selectTicket(ticket)"
                    >
                      <!-- 顶部一行：左侧名称+票价 | 右侧数量控制器 -->
                      <div class="ticket-top-row">
                        <div class="ticket-left-info">
                          <div class="ticket-name">{{ getTicketDisplayName(ticket) }}</div>
                          <div class="ticket-price">
                            <span v-if="ticket.price === 0">{{ $t('register.amount.free') }}</span>
                            <span v-else>￥{{ (ticket.price / 100).toFixed(2) }}</span>
                          </div>
                        </div>

                        <!-- 选中后才显示数量控件 -->
                        <div v-if="selectedTicketId === ticket.id" class="ticket-step-wrapper">
                          <span
                            class="step-btn step-minus"
                            :class="{ 'btn-disabled': ticketCountMap[ticket.id] <= (ticket.minnum || 0) }"
                            @click.stop="handleStepMinus(ticket)"
                          >
                            <i class="el-icon-minus"></i>
                          </span>
                          <span class="step-num">{{ ticketCountMap[ticket.id] }}</span>
                          <span
                            class="step-btn step-plus"
                            :class="{ 'btn-disabled': ticketCountMap[ticket.id] >= ticket.stock }"
                            @click.stop="handleStepPlus(ticket)"
                          >
                            <i class="el-icon-plus"></i>
                          </span>
                        </div>
                      </div>

                      <!-- 有说明文字，展示分割线+描述 -->
                      <template v-if="ticket.summary">
                        <div class="ticket-divider"></div>
                        <div class="ticket-desc">{{ ticket.summary }}</div>
                      </template>
                    </div>
                  </div>
                </div>
              </el-col>

              <!-- 右侧：表单 -->
              <el-col :span="14" :xs="24">
                <div class="form-wrapper">
                  <div class="form-title-bar">
                    <h4 class="form-title">{{ $t('register.fillInfo') || '填写报名信息' }}</h4>
                    <span class="form-total-info">
                      {{ $t('register.ticketTotal', { count: currentTicketCount, price: calcTotalPrice }) }}
                    </span>                  
                  </div>

                  <!-- 根据 num 动态渲染多个报名表单 -->
                  <div
                    v-for="(guest, idx) in guests"
                    :key="idx"
                    class="attendee-form-item"
                    :class="{ 'attendee-form-item-first': idx === 0 }"
                  >
                    <div class="attendee-form-header" v-if="guests.length > 1">
                      <span class="attendee-form-title">{{ $t('register.attendee') || '报名人' }} {{ idx + 1 }}</span>
                      <span class="attendee-form-divider"></span>
                    </div>

                    <el-form
                      :ref="'form_' + idx"
                      :model="guest"
                      :rules="dynamicRules"
                      label-position="top"
                      class="basic-info-form"
                    >
                      <el-row :gutter="20">
                        <!-- 国籍字段：仅英文时显示 -->
                        <el-col
                          v-if="isEn"
                          :span="24"
                          :xs="24"
                        >
                          <el-form-item
                            label="Country"
                            prop="country"
                          >
                            <el-select
                              v-model="guest.country"
                              placeholder="Please select country"
                              filterable
                              clearable
                              style="width:100%"
                            >
                              <el-option
                                v-for="(item, index) in countryOptions"
                                :key="index"
                                :label="item.name_en"
                                :value="item.value"
                              />
                            </el-select>
                          </el-form-item>
                        </el-col>

                        <!-- 其他字段 -->
                        <el-col
                          v-for="field in visibleFields"
                          :key="field.name"
                          :span="field.span || 24"
                          :xs="24"
                        >
                          <el-form-item
                            v-if="field.type !== 'select'"
                            :label="field.label"
                            :prop="field.name"
                            :required="field.required"
                          >
                            <el-input
                              v-model="guest[field.name]"
                              :placeholder="field.placeholder || field.label"
                              :type="field.name === 'email' ? 'email' : 'text'"
                            />
                          </el-form-item>

                          <el-form-item
                            v-else
                            :label="field.label"
                            :prop="field.name"
                            :required="field.required"
                          >
                            <el-select
                              v-model="guest[field.name]"
                              :placeholder="'请选择' + field.label"
                              style="width:100%"
                            >
                              <el-option
                                v-for="(label, value) in field.options"
                                :key="value"
                                :label="label"
                                :value="value"
                              />
                            </el-select>
                          </el-form-item>
                        </el-col>

                        <!-- 🔥 问卷题目（从第一个问题开始，第一个问题作为开关） -->
                        <el-col
                          v-for="(question, qIndex) in questionList"
                          :key="question.id"
                          :span="question.span || 24"
                          :xs="24"
                        >
                          <!-- 第一个问题作为开关，始终显示 -->
                          <el-form-item
                            v-if="qIndex === 0"
                            :label="question.title"
                            :required="question.required"
                          >
                            <el-radio-group
                              v-model="surveyForm[question.id]"
                              @change="(val) => handleHotelSwitch(question.id, val)"
                            >
                              <el-radio
                                v-for="opt in question.options"
                                :key="opt.value"
                                :label="opt.value"
                              >
                                {{ opt.label }}
                              </el-radio>
                            </el-radio-group>
                          </el-form-item>

                          <!-- 后续问题：仅当第一个问题选择了"是"（值不等于"B"）时显示 -->
                          <el-form-item
                            v-else
                            v-show="needHotelRoom"
                            :label="question.title"
                            :required="question.required && needHotelRoom"
                          >
                            <!-- 单选 radio -->
                            <template v-if="question.type === 'radio'">
                              <el-radio-group
                                v-model="surveyForm[question.id]"
                                @change="(val) => submitRadio(question.id, val, false, '', question.options)"
                              >
                                <el-radio
                                  v-for="opt in question.options"
                                  :key="opt.value"
                                  :label="opt.value"
                                >
                                  {{ opt.label }}
                                </el-radio>
                              </el-radio-group>
                            </template>

                            <!-- 文本 text -->
                            <template v-else-if="question.type === 'text'">
                              <el-input
                                v-model="surveyForm[question.id]"
                                :placeholder="getPlaceholder(question)"
                                @blur="submitText(question.id, surveyForm[question.id])"
                              ></el-input>
                            </template>

                            <!-- 日期 date -->
                            <template v-else-if="question.type === 'date'">
                              <el-date-picker
                                v-model="surveyForm[question.id]"
                                type="date"
                                value-format="yyyy-MM-dd"
                                :placeholder="getPlaceholder(question)"
                                style="width:100%"
                                @change="(val) => submitDate(question.id, val)"
                              ></el-date-picker>
                            </template>
                          </el-form-item>
                        </el-col>

                      </el-row>
                    </el-form>
                  </div>

                  <!-- 提交按钮 -->
                  <div class="form-actions mt-30 text-center">
                    <el-button type="primary" size="large" @click="submitForm" :disabled="isSubmitting">
                      {{ $t('register.submit') || '提交报名' }}
                    </el-button>
                    <el-button size="large" @click="resetForm">
                      {{ $t('register.reset') || '重置' }}
                    </el-button>
                  </div>
                </div>
              </el-col>
            </el-row>
          </template>

          <template v-else-if="event && event.closetime < Math.floor(Date.now() / 1000)">            
            <el-empty description="会议已结束~"></el-empty>
          </template>

          <!-- 支付弹窗 -->
          <PaymentDialog
            :visible.sync="showPaymentPanel"
            :order-id="orderId"
            :order-subject="orderSubject"
            :order-amount="orderAmount"
            @payment-success="handlePaymentCompleted"
            @payment-cancel="onPaymentCancel"
          />
          
        </div>
      </div>
    </section>
  </div>
</template>

<script>
import config from '@/config';
import EventInfo from "@/components/Event/Info.vue";
import countryData from '@/components/Data/country.json';
import PaymentDialog from '@/components/Order/PaymentDialog.vue'
import { getEventSettings, getEventTicketList, getPaychannels } from "@/api/event";
import { getOrder, postOrder, postCharge} from '@/api/order';
import { getMeOrders } from '@/api/user';
import profileService from '@/utils/profileService';
import { formatTimestampToDate } from '@/utils/dateUtil';
import { eventBus } from '@/utils/eventBus.js';
import { getSurveys, getQuestions, postAnswer, postSurveySubmit } from '@/api/survey';

// 默认字段配置（不包含国籍）
const DEFAULT_FIELDS = [
  { label: '姓名', name: 'realname', required: true, sort: 99 },
  { label: '性别', name: 'gender', required: false, sort: 10, type: 'select', options: { '1': '男', '2': '女' } },
  { label: '手机号', name: 'cellphone', required: true, sort: 98 },
  { label: '单位', name: 'company', required: false, sort: 0 },
  { label: '职务', name: 'position', required: false, sort: 0 },
  { label: '职称', name: 'title', required: false, sort: 0 },
  { label: '电子邮箱', name: 'email', required: false, sort: 0 },
  { label: '部门', name: 'department', required: false, sort: 0 },
  { label: '出生年月', name: 'birthdate', required: false, sort: 0 },
  { label: '证件类型', name: 'idtype', required: false, sort: 0, type: 'select', options: { '身份证': '身份证', '护照': '护照', '军官证': '军官证' } },
  { label: '证件号', name: 'idnumber', required: false, sort: 0 },
  { label: '民族', name: 'ethnicity', required: false, sort: 0 },
  { label: '地址', name: 'address', required: false, sort: 0 },
  { label: '其他备注', name: 'memo', required: false, sort: 0 },
];

export default {
  components: {
    EventInfo,
    PaymentDialog
  },
  inject: ['dataWrapper', 'fetchStatus', 'eventFetchStatus'],
  data() {
    return {
      isLoading: false,
      isSubmitting: false,
      eventid: config.EVENTID,
      event: null,
      fields: [],
      guests: [],
      ticketList: [],
      selectedTicketId: null,
      ticketCountMap: {},
      countryOptions: countryData,
      paymentQrcodeDataUrl: '',
      showPaymentPanel: false,
      selectedPaymentMethod: 'wechat',
      paymentLoading: false,
      paymentMethods: [
        { value: 'wechat', label: '微信支付' },
        { value: 'alipay', label: '支付宝支付' },
        {
          value: 'transfer', label: '对公转账',
          accountInfo: {}
        }
      ],
      ticket: { price: undefined },
      payment2id: '',
      payment3id: '',
      payment4id: '',
      orderId: '',
      orderSubject: '',
      orderAmount: 0,
      ticketFetched: false,
      ticketCache: null,
      alipayQrcodeDataUrl: '',
      currentLocale: localStorage.getItem('locale') || 'zh-cn',
      validOrders: [],

      // ========== 【动态问卷新增变量】==========
      surveyFormRef: null,
      sid: '', //问卷ID
      responseid: '', //答卷ID
      questionList: [], //问卷题目列表
      surveyForm: {}, //动态存储每题答案 key=question.id
      submittingQids: new Set(), //防问卷重复提交
      needHotelRoom: false, // 🔥 是否需要预留酒店房间，由第一个问题决定
      hotelQuestionId: '', // 🔥 第一个问题的ID（开关问题）
    };
  },
  computed: {
    userProfile() {
      return profileService.getProfile();
    },
    isEn() {
      const locale = this.$i18n?.locale || localStorage.getItem('locale') || 'zh-cn';
      return locale === 'en' || locale === 'en-US';
    },
    labelWidth() {
      return this.isEn ? '180px' : '100px';
    },
    currentTicketCount() {
      if (!this.selectedTicketId) return 0;
      return this.ticketCountMap[this.selectedTicketId] || 0;
    },
    calcTotalPrice() {
      if (!this.selectedTicketId || !this.ticket) return '0.00';
      const num = this.ticketCountMap[this.selectedTicketId] || 0;
      const price = this.ticket.price / 100;
      return (num * price).toFixed(2);
    },
    visibleFields() {
      return this.fields
        .filter(f => f.status === 1 && f.name !== 'country')
        .sort((a, b) => (Number(b.sort) || 0) - (Number(a.sort) || 0))
        .map(field => {
          const span = 24;
          const i18nKey = `register.${field.name}`;
          const label = this.$t(i18nKey);
          return {
            ...field,
            label: this.isEn ? label : field.label,
            span: span,
          };
        });
    },
    dynamicRules() {
      const rules = {};
      this.visibleFields.forEach(field => {
        const fieldRules = [];
        if (field.required) {
          fieldRules.push({ required: true, message: `请输入${field.label}`, trigger: 'blur' });
        }
        if (field.name === 'cellphone') {
          fieldRules.push({ pattern: /^1[3-9]\d{9}$/, message: '请输入正确的手机号码', trigger: 'blur' });
        }
        if (field.name === 'email') {
          fieldRules.push({ pattern: /^[a-zA-Z0-9_.-]+@[a-zA-Z0-9-]+(\.[a-zA-Z0-9-]+)*\.[a-zA-Z0-9]{2,6}$/, message: '请输入正确的电子邮箱格式', trigger: 'blur' });
        }
        if (fieldRules.length > 0) {
          rules[field.name] = fieldRules;
        }
      });
      if (this.isEn) {
        rules['country'] = [
          { required: true, message: 'Please select country', trigger: 'change' }
        ];
      }
      return rules;
    }
  },
  created() {
    const savedLocale = localStorage.getItem('locale');
    if (savedLocale) {
      this.currentLocale = savedLocale;
    } else if (this.$i18n?.locale) {
      this.currentLocale = this.$i18n.locale;
    }

    eventBus.$on('event-ready', this.onEventReady);
    eventBus.$on('language-changed', (lang) => {
      this.currentLocale = lang;
      this.$nextTick(() => {
        if (this.$refs.basicForm) {
          this.$refs.basicForm.clearValidate();
        }
      });
    });

    if (this.$i18n && this.$i18n.on) {
      this.$i18n.on('languageChanged', (lang) => {
        this.currentLocale = lang;
        this.$nextTick(() => {
          if (this.$refs.basicForm) {
            this.$refs.basicForm.clearValidate();
          }
        });
      });
    }

    Promise.allSettled([
      this.fetchTicketList(),
      this.fetchEventSettings(),
      this.loadAllValidOrders(),
      this.loadSurveyInfo()
    ])
  },
  watch: {
    currentTicketCount: {
      handler(newVal) {
        this.updateGuests(newVal);
      },
      immediate: true,
    },
    userProfile: {
      handler(newVal) {
        if (newVal && this.guests.length > 0) {
          const mapping = {
            realname: 'realname',
            gender: 'gender',
            cellphone: 'cellphone',
            company: 'company',
            department: 'department',
            position: 'position',
            title: 'title',
            email: 'email',
          };
          Object.keys(mapping).forEach(key => {
            if (newVal[mapping[key]] !== undefined && !this.guests[0][key]) {
              this.$set(this.guests[0], key, newVal[mapping[key]]);
            }
          });
        }
      },
      immediate: true,
    },
    selectedPaymentMethod: {
      async handler(val) {
        this.paymentQrcodeDataUrl = '';
        if (val !== 'transfer') {
          if (val === 'alipay') {
            const res = await postCharge({
              orderid: this.orderId,
              paychannelid: this.payment2id,
            });
            if (res.code === 201) {
              this.alipayQrcodeDataUrl = res.data.qr_code;
            }
          }
        }
      }
    },
    '$i18n.locale': {
      handler(newVal) {
        this.currentLocale = newVal;
        this.$nextTick(() => {
          if (this.$refs.basicForm) {
            this.$refs.basicForm.clearValidate();
          }
        });
      },
      immediate: true,
    },
    validOrders:{
      handler(val){
        console.warn("validOrders发生变化！长度=", val.length)
      },
      deep:true
    }
  },
  beforeDestroy() {
    if (this.$i18n && this.$i18n.off) {
      this.$i18n.off('languageChanged');
    }
    eventBus.$off('event-ready', this.onEventReady);
    eventBus.$off('language-changed');
  },
  methods: {
    formatTimestampToDate,
    handleStepMinus(ticket) {
      const minNum = ticket.minnum ?? 0;
      let val = this.ticketCountMap[ticket.id];
      if (val <= minNum) return;
      val--;
      this.$set(this.ticketCountMap, ticket.id, val);
      this.onCountChange(ticket);
    },
    handleStepPlus(ticket) {
      const maxNum = ticket.stock ?? 10;
      let val = this.ticketCountMap[ticket.id];
      if (val >= maxNum) return;
      val++;
      this.$set(this.ticketCountMap, ticket.id, val);
      this.onCountChange(ticket);
    },
    updateGuests(count) {
      const currentLength = this.guests.length;
      if (count > currentLength) {
        for (let i = currentLength; i < count; i++) {
          const guest = {};
          this.visibleFields.forEach(field => {
            this.$set(guest, field.name, '');
          });
          if (this.isEn) {
            this.$set(guest, 'country', '');
          }
          this.guests.push(guest);
        }
      } else if (count < currentLength) {
        this.guests.splice(count);
      }
    },
    onCountChange(ticket) {
      if (this.ticketCountMap[ticket.id] === 0) {
        if (this.selectedTicketId === ticket.id) {
          this.selectedTicketId = null;
        }
      } else {
        this.selectedTicketId = ticket.id;
      }
    },
    async fetchTicketList() {
      try {
        const res = await getEventTicketList(this.eventid);
        console.log('获取票务列表:', res);
        if (res.code < 300 && res.list && res.list.length > 0) {
          const nowTs = Math.floor(Date.now() / 1000); // 当前秒级时间戳
          // 过滤：只保留在销售时间段内的票
          this.ticketList = res.list.filter(ticket => {
            const start = ticket.salestart;
            const close = ticket.saleclose;
            return nowTs >= start && nowTs <= close;
          });

          this.ticketList.forEach(ticket => {
            if (this.ticketCountMap[ticket.id] === undefined) {
              this.$set(this.ticketCountMap, ticket.id, 0);
            }
          });
          if (this.ticketList.length > 0) {
            this.selectTicket(this.ticketList[0]);
          }
        }
      } catch (error) {
        console.error('获取票务列表失败:', error);
      }
    },
    selectTicket(ticket) {
      this.selectedTicketId = ticket.id;
      this.ticket = ticket;
      const minNum = ticket.minnum ?? 1;
      this.$set(this.ticketCountMap, ticket.id, minNum);
    },
    async fetchEventSettings() {
      try {
        const result = await getEventSettings(this.eventid);
        if (result.code < 300) {
          const res = result.data;
          const requiredList = res?.badgefields?.requires || [];
          const mergedFields = DEFAULT_FIELDS.map(defaultField => {
            const backendField = requiredList.find(item => item.name === defaultField.name);
            if (backendField) {
              return {
                ...defaultField,
                ...backendField,
                status: 1,
              };
            }
            if (defaultField.required) {
              return {
                ...defaultField,
                status: 1,
              };
            }
            return { ...defaultField, status: 0 };
          });

          this.fields = mergedFields;

          if (this.guests.length === 0) {
            const guest = {};
            this.fields.forEach(field => {
              this.$set(guest, field.name, '');
            });
            this.guests.push(guest);
          }
        }
      } catch (error) {
        console.error('获取事件设置失败:', error);
        this.fields = DEFAULT_FIELDS.map(f => ({ ...f, status: f.required ? 1 : 0 }));
        if (this.guests.length === 0) {
          const guest = {};
          this.fields.forEach(field => {
            this.$set(guest, field.name, '');
          });
          this.guests.push(guest);
        }
      }
    },

    //=====================问卷相关方法开始=====================
    /** 获取placeholder */
    getPlaceholder(item) {
      const attr = item.attributes?.find(a => a.key === 'placeholder')
      return attr?.value || ''
    },
    /** 加载问卷信息 */
    async loadSurveyInfo() {
      try {
        const res = await getSurveys(this.eventid, { filter: { status: 100 } })
        if (!res.data?.list?.length) return
        const survey = res.data.list[0]
        this.sid = survey.id
        const qRes = await getQuestions(this.sid)
        this.questionList = qRes.data?.list || []
        console.log('问卷列表', this.questionList)
        
        // 🔥 设置第一个问题为酒店开关问题
        if (this.questionList.length > 0) {
          this.hotelQuestionId = this.questionList[0].id
        }
      } catch (err) {
        console.log('暂无配置问卷', err)
      }
    },
    /** 处理酒店开关切换 */
    handleHotelSwitch(qid, value) {
      // 判断是否选择了"否"（根据截图，value为"B"表示不需要）
      this.needHotelRoom = value !== 'B'
      
      // 如果选择了"否"，清空后续问题的答案
      if (!this.needHotelRoom) {
        this.questionList.forEach((q, index) => {
          if (index > 0) {
            this.$set(this.surveyForm, q.id, '')
          }
        })
      }
      
      // 提交第一个问题的答案
      this.submitRadio(qid, value, false, '', this.questionList[0]?.options)
    },
    /** 单选提交 */
    async submitRadio(qid, value, other, memo, options) {
      if(this.submittingQids.has(qid)) return
      this.submittingQids.add(qid)
      try{
        let realLabel = value
        let realValue = value
        if(Array.isArray(options)){
          const opt = options.find(item => item.value === value)
          if(opt){
            realLabel = opt.label
            realValue = opt.value
          }
        }
        console.log('realLabel', realLabel, 'realValue', realValue)

        const params = other 
          ? { 'answer': { 'OTHER': memo } } 
          : { 'answer': { [realValue]: realLabel } };

        const res = await postAnswer(this.sid, qid, params);
        if (res.code === 201) {
          this.responseid = res.data.id;
        }
      }finally{
        this.submittingQids.delete(qid)
      }
    },
    /** 文本题目提交 */
    async submitText(qid, value) {
      if(this.submittingQids.has(qid)) return
      this.submittingQids.add(qid)
      try{
        const params = { answer: { text: value } }
        const res = await postAnswer(this.sid, qid, params)
        if (res.code === 201) {
          this.responseid = res.data.id
        }
      }finally{
        this.submittingQids.delete(qid)
      }
    },
    /** 日期题目提交 */
    async submitDate(qid, value) {
      if(this.submittingQids.has(qid)) return
      this.submittingQids.add(qid)
      try{
        const params = { answer: { date: value } }
        const res = await postAnswer(this.sid, qid, params)
        if (res.code === 201) {
          this.responseid = res.data.id
        }
      }finally{
        this.submittingQids.delete(qid)
      }
    },
    //=====================问卷相关方法结束=====================

    submitForm() {
      let validAll = true;
      const formRefList = [];
      for (let i = 0; i < this.guests.length; i++) {
        const formRef = this.$refs['form_' + i];
        if (formRef && formRef[0]) {
          formRefList.push(formRef[0]);
          formRef[0].validate((valid) => {
            if (!valid) validAll = false;
          });
        }
      }

      if (!validAll) {
        this.$message.error('请完善所有报名人信息');
        return;
      }

      // ==========【问卷必填项校验】==========
      if (this.questionList.length > 0) {
        // 🔥 1. 第一个问题（酒店开关）必须填写
        const firstQuestion = this.questionList[0];
        const firstValue = this.surveyForm[firstQuestion.id];
        if (firstValue === null || firstValue === undefined || firstValue === '') {
          this.$message.error(`请选择：${firstQuestion.title}`);
          return;
        }

        // 🔥 2. 只有需要酒店房间时，才校验后续问题的必填
        if (this.needHotelRoom) {
          for (let i = 1; i < this.questionList.length; i++) {
            const q = this.questionList[i];
            if (q.required) {
              const val = this.surveyForm[q.id];
              if (val === null || val === undefined || val === '') {
                this.$message.error(`请填写：${q.title}`);
                return;
              }
            }
          }
        }
      }

      this.isSubmitting = true;
      this.createOrder()
        .then(async (orderId) => {
          this.orderId = orderId;
          this.$message.success('报名信息校验完成，订单创建成功');
          await this.fetchPaymentsData(this.orderId);
          if (!this.orderId || !this.payment3id) {
            this.$message.error('支付配置信息获取失败，请稍后重试');
            this.isSubmitting = false;
            return;
          }
          const orderResult = await getOrder(this.orderId);
          const orderData = orderResult.data;

          // 提交问卷
          await postSurveySubmit(this.sid, {
            refid: orderData.id,
            refobj: 'order',
            baseinfo: [
              { label: 'cellphone', value: orderData.cellphone },
              { label: 'realname', value: orderData.realname },
              { label: 'email', value: orderData.email }
            ],
          });

          if (orderData.paystatus >= 99) {
            this.$message.success('报名成功！');
            this.$router.replace('/register/result');
          } else {
            this.showPaymentPanel = true;
          }
        })
        .catch(err => {
          console.error('创建订单失败：', err);
          this.$message.error(err.message || '订单创建失败，请检查信息后重试');
        })
        .finally(() => {
          this.isSubmitting = false;
        });
    },
    resetForm() {
      this.guests.forEach((item, idx) => {
        const formRef = this.$refs['form_' + idx];
        if (formRef && formRef[0]) {
          formRef[0].resetFields();
        }
      });
      this.surveyForm = {};
      this.needHotelRoom = false;

      Object.keys(this.ticketCountMap).forEach(key => {
        this.$set(this.ticketCountMap, key, 0);
      });
      this.selectedTicketId = null;
    },
    onEventReady(eventData) {
      this.event = eventData;
      this.isLoading = false;
    },
    async fetchPaymentsData(orderId) {
      try {
        const res = await getPaychannels(orderId || this.orderId);
        if (res.code === 200 && res.list && res.list.length > 0) {
          const payment3Item = res.list.find(item => item.paysource === 3);
          if (payment3Item && payment3Item.id) {
            this.payment3id = payment3Item.id;
          }
          const payment2Item = res.list.find(item => item.paysource === 2);
          if (payment2Item && payment2Item.id) {
            this.payment2id = payment2Item.id;
          }
          const payment4Item = res.list.find(item => item.paysource === 4);
          if (payment4Item && payment4Item.id) {
            this.payment4id = payment4Item.id;
          }
          if (payment4Item && payment4Item?.name) {
            this.paymentMethods[2].accountInfo.accountName = payment4Item?.name;
          }
          if (payment4Item && payment4Item?.no) {
            this.paymentMethods[2].accountInfo.accountNo = payment4Item?.no;
          }
          if (payment4Item && payment4Item?.bank) {
            this.paymentMethods[2].accountInfo.bankName = payment4Item?.bank;
          }
        } else {
          throw new Error('获取支付渠道列表失败');
        }
      } catch (error) {
        console.error('获取支付配置失败:', error);
        this.$message.error('获取支付配置失败，请重试');
        throw error;
      }
    },
    async createOrder() {
      try {
        // 处理 guests 数据，转换字段类型
        const guests = this.guests.map(guest => {
          const processedGuest = { ...guest };
          
          // 处理 gender 字段：转为数字 (0, 1, 2)，如果为空则删除
          if (processedGuest.gender !== undefined && processedGuest.gender !== null && processedGuest.gender !== '') {
            processedGuest.gender = Number(processedGuest.gender);
          } else {
            delete processedGuest.gender;
          }
          
          // 如果后端需要 idtype 为数字，同样处理
          if (processedGuest.idtype !== undefined && processedGuest.idtype !== null && processedGuest.idtype !== '') {
            processedGuest.idtype = Number(processedGuest.idtype);
          } else {
            delete processedGuest.idtype;
          }
          
          return processedGuest;
        });
        
        const metas = { 
          'guests': guests,
        };
        const params = {
          responseid: this.responseid,
          type: 1,
          cellphone: this.guests[0].cellphone,
          realname: this.guests[0].realname,
          title: this.guests[0].title,
          company: this.guests[0].company,
          email: this.guests[0].email,
          cartitems: [
            {
              goodid: this.ticket.id,
              goodnum: this.ticketCountMap[this.ticket.id],
              quantity: this.ticketCountMap[this.ticket.id],
              guests: guests,
            },
          ],
          metaitems: metas
        };
        const orderRes = await postOrder(params);
        if (orderRes.code >= 300) {
          throw new Error(orderRes.msg || '创建订单失败');
        }
        this.orderSubject = orderRes.data.subject || '订单支付';
        this.orderAmount = orderRes.data.amount || 0;
        return orderRes.data.id;
      } catch (error) {
        console.error('创建订单失败:', error);
        throw new Error(error.message || '创建订单失败，请稍后重试');
      }
    },
    async handlePaymentCompleted() {
      this.paymentLoading = true;
      try {
        const result = await getOrder(this.orderId);
        const res = result.data;
        if (res.paystatus == 99 || res.paystatus == 100 || res.paystatus == 50) {
          this.$message.success('支付确认提交成功');
          this.showPaymentPanel = false;
          this.selectedPaymentMethod = 'wechat';
          // 支付成功后跳转到结果页
          this.$router.replace('/register/result');
        } else {
          this.$message.error('订单未支付，请先支付订单');
        }
      } catch (error) {
        console.error('支付确认失败:', error);
        this.$message.error('支付确认提交失败，请稍后重试');
      } finally {
        this.paymentLoading = false;
      }
    },
    onPaymentCancel() {
      console.log('用户取消了支付');
    },
    getPaymentQrcodeUrl() {
      if (!this.orderId || !this.payment3id) {
        return '';
      }
      if (this.selectedPaymentMethod === 'wechat') {
        return `${config.WX_BASE_URL}pay?id=${this.orderId}&paychannelid=${this.payment3id}`;
      } else if (this.selectedPaymentMethod === 'alipay') {
        return this.alipayQrcodeDataUrl;
      }
      return '';
    },
    getTransferQrcodeUrl() {
      if (!this.orderId) {
        return '';
      }
      return `${config.WX_BASE_URL}order?id=${this.orderId}`;
    },
    onPaymentQrcodeChange(dataUrl) {
      console.log('支付二维码:', dataUrl);
      this.paymentQrcodeDataUrl = dataUrl;
    },
    getTicketDisplayName(ticket) {
      if (this.isEn) {
        // 如果是 Regular Delegate 的ID
        if (ticket.id === '18c48b30-b81c-c0ee-c636-9d883fe6ef4a') {
          return 'Regular Delegate';
        }
        // 如果是 Student Delegate 的ID
        if (ticket.id === '18c48b38-9baa-3e02-9da0-25ea0669bc66') {
          return 'Student Delegate';
        }
        // 其他票种，如果有英文名称字段则使用，否则使用 fullname
        return ticket.fullname;
      }
      // 中文环境下显示原始名称
      return ticket.fullname;
    },
    /**
     * 加载所有有效订单
     */
     async loadAllValidOrders() {
      try {
        const meOrdersRes = await getMeOrders({
          filter: {
            validstatus: true,
            status: 0,
            type: 1
          }
        });

        if (meOrdersRes.code < 300 && meOrdersRes.data && meOrdersRes.data.list.length > 0) {
          const allOrders = meOrdersRes.data.list;
          const validOrders = allOrders.filter(order =>
            order.eventid === this.eventid &&
            order.type === 1 &&
            order.status >= 0 &&
            (order.paystatus > 98 ||
             (order.expiredat > Math.floor(Date.now() / 1000) && order.paystatus < 99))
          );
          this.validOrders = [...validOrders];
          console.log('orders:', this.validOrders);

          this.$nextTick(() => {
            console.log('nextTick校验长度：', this.validOrders.length);
          });
        } else {
          this.validOrders = [];
        }

      } catch (error) {
        this.validOrders = [];
      }
    },
  }
};
</script>

<style scoped>
.pc-padding {
  padding-left: 150px;
  padding-right: 150px;
}

@media (max-width: 768px) {
  .pc-padding {
    padding-left: 20px;
    padding-right: 20px;
  }
}

/* ===== 票务列表外层 ===== */
.ticket-list-wrapper {
  background: #f8fafc;
  border-radius: 12px;
  padding: 16px;
  border: 1px solid #e8ecf1;
}

.ticket-list-header h4 {
  font-size: 20px;
  font-weight: 600;
  color: #1a3a6b;
  margin-bottom: 14px;
}

.ticket-list {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.ticket-item {
  background: #ffffff;
  border-radius: 18px;
  padding: 15px;
  border: 2px solid #e6e8f0;
  cursor: pointer;
  transition: all 0.22s ease;
}
.ticket-item.ticket-active {
  border-color: #82b6ff;
  background-color: #ffffff;
}

.ticket-top-row {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
}

.ticket-left-info {
  flex-shrink: 0;
}
.ticket-name {
  font-size: 16px;
  font-weight: 500;
  color: #111111;
  line-height: 1.2;
}
.ticket-price {
  font-size: 20px;
  font-weight: 600;
  color: #f25c5c;
  margin-top: 6px;
  line-height: 1.2;
}

.ticket-step-wrapper {
  display: flex;
  align-items: center;
  gap: 16px;
  flex-shrink: 0;
}
.step-btn {
  width: 24px;
  height: 24px;
  border-radius: 50%;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  font-size: 16px;
  transition: all 0.2s;
}
.step-minus {
  border: 2px solid #d1d1d1;
  color: #888;
  background: #fff;
}
.step-plus {
  background-color: #409EFF;
  color: #ffffff;
  border: 2px solid #409EFF;
}
.step-btn.btn-disabled {
  cursor: not-allowed;
  opacity: 0.45;
}
.step-minus.btn-disabled {
  border-color: #e0e0e0;
  color: #bbb;
}
.step-plus.btn-disabled {
  background-color: #b3d4f7;
  border-color: #b3d4f7;
  color: #ffffff;
}
.step-num {
  font-size: 18px;
  min-width: 18px;
  text-align: center;
  color: #111;
}

.ticket-divider {
  height: 1px;
  background: #e5e7eb;
  margin: 14px 0 14px;
}
.ticket-desc {
  font-size: 14px;
  color: #666;
  line-height: 1.6;
  white-space: pre-line;
}

/* ===== 报名人表单样式 ===== */
.attendee-form-item {
  margin-bottom: 24px;
  padding: 20px;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e8ecf1;
}

.attendee-form-item-first {
  margin-top: 0;
}

.attendee-form-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 2px solid #e8ecf1;
}

.attendee-form-title {
  font-size: 16px;
  font-weight: 600;
  color: #1a3a6b;
}

.attendee-form-divider {
  flex: 1;
  margin-left: 12px;
}

.el-tag {
  color: #ffffff;
}

/* ===== 表单容器样式 ===== */
.form-wrapper {
  padding: 0 10px;
}

.form-title-bar {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 20px;
  padding-bottom: 12px;
  border-bottom: 2px solid #f0f4f8;
}
.form-title {
  font-size: 18px;
  font-weight: 600;
  color: #1a3a6b;
  margin: 0;
}
.form-total-info {
  font-size: 16px;
  color: #333;
}

.form-actions {
  margin-top: 30px;
  padding-top: 20px;
  border-top: 1px solid #f0f4f8;
}

@media (max-width: 768px) {
  .ticket-list-wrapper {
    margin-bottom: 20px;
  }
  .form-wrapper {
    padding: 0;
  }
  .attendee-form-item {
    padding: 12px;
  }
  .ticket-step-wrapper {
    justify-content: flex-start;
  }
}

/* 支付面板样式 */
.payment-container {
  text-align: center;
}
.payment-header {
  text-align: center;
  margin-bottom: 20px;
}
.price {
  font-size: 24px;
  font-weight: bold;
  color: #F56C6C;
  margin-top: 10px;
}
.payment-methods {
  margin-bottom: 20px;
  padding-bottom: 15px;
  border-bottom: 1px solid #eee;
  display: flex;
  gap: 20px;
}
.payment-methods ::v-deep .el-radio {
  font-size: 16px;
}
.qrcode-section {
  text-align: center;
  padding: 10px 0;
}
.payment-qrcode {
  width: 200px;
  height: 200px;
  margin: 0 auto;
}
.dialog-footer {
  display: flex;
  justify-content: center;
  gap: 10px;
}
.mt-5 {
  margin-top: 5px;
}
.mt-10 {
  margin-top: 10px;
}
.mb-10 {
  margin-bottom: 10px;
}
.mb-15 {
  margin-bottom: 15px;
}
.text-red {
  color: #F56C6C;
  font-size: 12px;
}
:deep(.el-dialog__body){
  padding: 0 20px 20px 20px;
}
.registered-notice-block {
  margin-bottom: 10px;
}
:deep(.el-alert){
  border-radius: 12px;
}
</style>